import {FormArray, FormControl, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {Image} from '@sportngin/se-commerce-sdk/models/clare';
import {SeFeFormFieldConfig} from 'se-fe-form-field';

export type ImageFormGroup = FormGroup<{src: FormControl<string>; alt: FormControl<string>}>;

export const MAX_IMAGE_UPLOAD_COUNT = 10;

/** Alert text when the upload limit is reached. */
export const IMAGE_UPLOAD_LIMIT_ERROR_MESSAGE = `A maximum of ${MAX_IMAGE_UPLOAD_COUNT} images can be added`;

/** SeFeAlert subheader text when the upload limit is exceeded */
export const getImageUploadLimitSubheaderMessage = (rejectedCount: number, addedCount: number): string => {
  const rejectedPart = rejectedCount === 1 ? '1 image was' : `${rejectedCount} images were`;
  const addedPart = addedCount === 1 ? '1 image has been added' : `${addedCount} images have been added`;
  if (addedCount <= 0) {
    return `${rejectedPart} not added.`;
  }
  return `${addedPart}, ${rejectedPart} not added.`;
};

/**
 * Counts how many newly uploaded images cannot be added
 * Ignores images that are already present in the form array (e.g. re-emitted existing images).
 * @param formArray - the form array to count the rejected images in.
 * @param urls - the urls of the images to count the rejected images in.
 * @param maxImages - the maximum number of images allowed.
 * @returns the number of images that cannot be added.
 */
export const countRejectedImageUploadUrls = (
  formArray: FormArray<ImageFormGroup>,
  urls: string[],
  maxImages = MAX_IMAGE_UPLOAD_COUNT
): number => {
  const normalizedUrls = [...new Set(urls.map(url => url.trim()).filter(Boolean))];
  if (!normalizedUrls.length) {
    return 0;
  }

  const existingSrcs = new Set(formArray.controls.map(control => control.get('src')?.value ?? '').filter(src => !!src));
  const newUrls = normalizedUrls.filter(url => !existingSrcs.has(url));
  const remainingSlots = Math.max(0, maxImages - formArray.length);

  return Math.max(0, newUrls.length - remainingSlots);
};

/** Alert text when the image upload fails. */
export const IMAGE_UPLOAD_GENERIC_FAILURE_MESSAGE = 'Image upload failed. Please try again.';

const imageDescriptionErrorMessages = {
  required: 'Please enter an image description',
  maxlength: 'Description exceeds maximum length allowed',
};

const imageDescriptionConfigCache = new Map<number, SeFeFormFieldConfig>();

const defaultAltValidators = [Validators.maxLength(200)];

export const getImageDescriptionConfig = (index: number): SeFeFormFieldConfig => {
  const imageNumber = index + 1;
  const cachedConfig = imageDescriptionConfigCache.get(imageNumber);
  const config = {
    label: `Image ${imageNumber} description`,
    required: true,
    errorMessages: imageDescriptionErrorMessages,
  };
  if (cachedConfig) {
    return cachedConfig;
  }
  imageDescriptionConfigCache.set(imageNumber, config);
  return config;
};

export const createImageFormGroup = (
  src = '',
  alt = '',
  altValidators: ValidatorFn[] = defaultAltValidators
): ImageFormGroup => {
  return new FormGroup({
    src: new FormControl<string>(src, {nonNullable: true}),
    alt: new FormControl<string>(alt, {
      nonNullable: true,
      validators: altValidators,
    }),
  });
};

export const mapImageFormArrayToImages = (formArray: FormArray<ImageFormGroup>): Image[] => {
  return formArray.controls
    .map(group => ({
      src: group.get('src')?.value ?? '',
      alt: group.get('alt')?.value ?? '',
    }))
    .filter(image => !!image.src);
};

export const replaceImageFormArray = (
  formArray: FormArray<ImageFormGroup>,
  images: Image[],
  createGroup: (src?: string, alt?: string) => ImageFormGroup
): void => {
  formArray.clear();
  for (const image of images) {
    formArray.push(createGroup(image.src ?? '', image.alt ?? ''));
  }
};

export const appendUniqueUploadedImages = (
  formArray: FormArray<ImageFormGroup>,
  urls: string[],
  defaultAlt: string,
  createGroup: (src?: string, alt?: string) => ImageFormGroup,
  replaceExisting = false,
  maxImages = MAX_IMAGE_UPLOAD_COUNT
): boolean => {
  const normalizedUrls = urls.map(url => url.trim()).filter(Boolean);
  if (!normalizedUrls.length) {
    return false;
  }

  if (replaceExisting) {
    formArray.clear();
  }

  const existingSrcs = new Set(formArray.controls.map(control => control.get('src')?.value ?? '').filter(src => !!src));
  let remainingSlots = Math.max(0, maxImages - formArray.length);

  let uploadAccepted = false;
  for (const url of new Set(normalizedUrls)) {
    if (remainingSlots <= 0) {
      break;
    }

    if (existingSrcs.has(url)) {
      continue;
    }

    formArray.push(createGroup(url, defaultAlt));
    existingSrcs.add(url);
    remainingSlots--;
    uploadAccepted = true;
  }

  return uploadAccepted;
};

export const hasReachedImageUploadLimit = (
  formArray: FormArray<ImageFormGroup>,
  maxImages = MAX_IMAGE_UPLOAD_COUNT
): boolean => {
  return formArray.length >= maxImages;
};

export const remainingImageUploadSlots = (
  formArray: FormArray<ImageFormGroup>,
  maxImages = MAX_IMAGE_UPLOAD_COUNT
): number => {
  return Math.max(0, maxImages - formArray.length);
};

export const trimImageAltValues = (formArray: FormArray<ImageFormGroup>): void => {
  formArray.controls.forEach(group => {
    const altControl = group.get('alt');
    altControl?.setValue((altControl.value ?? '').trim(), {emitEvent: false});
  });
};
